'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use", "showTimeInput"];
import convertToInt from '../utils/convertToInt';
import { CUSTOM } from '../constants/RelativeDates';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'styled-components';
import filter from 'transmute/filter';
import getIn from 'transmute/getIn';
import { fromMoment, toMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UIMicroTimeInput from 'UIComponents/input/UIMicroTimeInput';
import UISelect from 'UIComponents/input/UISelect';
import UIBox from 'UIComponents/layout/UIBox';
import { Flex } from 'foundations-components/components/Flex';
import { List } from 'foundations-components/transitional/components/List';
import { Box } from 'foundations-components/transitional/components/Box';
import { TimeInput } from 'foundations-components/transitional/components/TimeInput';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UIMicroDateInput from 'UIComponents/dates/UIMicroDateInput';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InlineFlex = styled.div.withConfig({
  displayName: "TaskDueDateInput__InlineFlex",
  componentId: "sc-1ospzc2-0"
})(["display:inline-flex;align-items:baseline;"]);

// To be used with ../utils/RelativeDatePresets
export default class TaskDueDateInput extends PureComponent {
  constructor(props) {
    super(props);
    this.handleSelectChange = evt => {
      if (evt.target.value === CUSTOM) {
        this.setState({
          forceAbsoluteInput: true
        });
        return;
      }
      const newValue = this.getDate(evt.target.value).minutes(this.getTimeMins()).valueOf();
      this.props.onChange({
        target: {
          value: String(newValue)
        }
      });
    };
    this.handleDateChange = evt => {
      if (evt.target.value === null) {
        if (this.props.defaultValue) {
          this.props.onChange(syntheticEvent(String(this.props.defaultValue)));
        } else if (this.props.options[0]) {
          this.props.onChange(syntheticEvent(String(this.getDate(getIn([0, 'value'], this.props.options)).minutes(this.getTimeMins()).valueOf())));
        } else {
          this.props.onChange(syntheticEvent(String(this.getDate(I18n.moment.userTz().valueOf()).minutes(this.getTimeMins()).valueOf())));
        }
        this.setState({
          forceAbsoluteInput: false
        });
        return;
      }
      const timestamp = toMoment(evt.target.value).valueOf();
      const newValue = this.getDate(timestamp).minutes(this.getTimeMins()).valueOf();
      this.props.onChange(syntheticEvent(String(newValue)));
    };
    this.handleTimeChange = evt => {
      const newValue = this.getDate()
      // If the inputted time is cleared, value can be null
      .minutes(evt.target.value || 0).valueOf();
      this.props.onChange({
        target: {
          value: String(newValue)
        }
      });
    };
    this.state = {
      forceAbsoluteInput: false
    };
  }
  shouldShowAbsoluteDateInput() {
    return (
      // reached by clicking "custom"
      this.state.forceAbsoluteInput ||
      // reached because the date doesn't match any of the presets
      !this.props.options.find(option => this.getDate(option.value).isSame(this.getDate(), 'day'))
    );
  }
  getOptionsForRender() {
    const customOption = {
      value: CUSTOM,
      text: I18n.text('taskFormsLib.input.taskDueDate.custom')
    };
    return [...this.props.options, customOption];
  }
  getValue(timestamp) {
    const {
      value,
      defaultValue
    } = this.props;
    return convertToInt(timestamp) || convertToInt(value) || convertToInt(defaultValue);
  }
  getDate(timestamp) {
    const value = this.getValue(timestamp);
    return I18n.moment.userTz(value).clone().startOf('day');
  }
  getTimeMins(timestamp) {
    const value = this.getValue(timestamp);
    const momentDate = I18n.moment.userTz(value);
    return momentDate.hour() * 60 + momentDate.minutes();
  }
  getOptionIfMatchesValue(value) {
    return this.props.options.find(option => this.getDate(option.value).isSame(this.getDate(value), 'day'));
  }
  renderAbsoluteDateInput() {
    const {
      autoFocus,
      dateInputProps,
      readOnly,
      disabled,
      error,
      defaultOpen,
      use
    } = this.props;
    if (use === 'textOnly') {
      return I18n.moment.userTz(this.getValue()).format('ll');
    }

    // Trellis: https://git.hubteam.com/HubSpot/foundations-components/issues/3517
    const DateInputComponent = use === 'link' || use === 'transparent' ? UIMicroDateInput : UIDateInput;
    return /*#__PURE__*/_jsx(DateInputComponent, Object.assign({}, dateInputProps, {
      "aria-labelledby": this.props['aria-labelledby'],
      autoFocus: this.state.forceAbsoluteInput || autoFocus,
      readOnly: readOnly,
      disabled: disabled,
      error: error,
      value: fromMoment(this.getDate()),
      onChange: this.handleDateChange,
      defaultOpen: defaultOpen
      // HACK: prevent ESC key presses from propagating up and cancelling changes
      // See https://git.hubteam.com/HubSpot/UIComponents/issues/9877
      ,
      onKeyDown: e => e.stopPropagation(),
      onKeyPress: e => e.stopPropagation(),
      onKeyUp: e => e.stopPropagation()
    }));
  }
  renderRelativeDateInput() {
    const {
      autoFocus,
      dateSelectProps,
      readOnly,
      disabled,
      error,
      defaultOpen,
      use
    } = this.props;
    const selectButtonUse = use === 'textOnly' ? 'link' : use;
    return /*#__PURE__*/_jsx(UISelect, Object.assign({
      "aria-labelledby": this.props['aria-labelledby'],
      buttonUse: selectButtonUse
    }, dateSelectProps, {
      autoFocus: autoFocus,
      readOnly: readOnly,
      disabled: disabled,
      error: error,
      menuWidth: "auto",
      searchable: false,
      value: this.getDate().valueOf(),
      onChange: this.handleSelectChange,
      options: this.getOptionsForRender(),
      defaultOpen: defaultOpen
    }));
  }
  renderTimeInput() {
    const {
      timeInputProps,
      readOnly,
      disabled,
      use,
      error
    } = this.props;
    if (use === 'textOnly') {
      return I18n.moment.userTz(this.getValue()).format('LT z');
    }
    const TimeInputComponent = use === 'link' || use === 'transparent' ? UIMicroTimeInput : TimeInput;
    return /*#__PURE__*/_jsx(TimeInputComponent, Object.assign({
      "aria-labelledby": this.props['aria-labelledby'],
      interval: 15
    }, timeInputProps, {
      readOnly: readOnly,
      disabled: disabled,
      error: error,
      value: this.getTimeMins(),
      onChange: this.handleTimeChange
    }));
  }
  render() {
    const _this$props = this.props,
      {
        use,
        showTimeInput
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const dataAttributes = filter((val, key) => key.startsWith('data-'), rest);
    if (use === 'textOnly') {
      return /*#__PURE__*/_jsxs(List, Object.assign({
        inline: true,
        use: "inline-divided"
      }, dataAttributes, {
        children: [this.shouldShowAbsoluteDateInput() ? this.renderAbsoluteDateInput() : this.renderRelativeDateInput(), showTimeInput && /*#__PURE__*/_jsx(UIBox, {
          children: this.renderTimeInput()
        })]
      }));
    }
    if (use === 'link' || use === 'transparent') {
      return /*#__PURE__*/_jsxs(InlineFlex, Object.assign({}, dataAttributes, {
        children: [this.shouldShowAbsoluteDateInput() ? this.renderAbsoluteDateInput() : this.renderRelativeDateInput(), showTimeInput && /*#__PURE__*/_jsx(UIBox, {
          className: "m-left-2",
          children: this.renderTimeInput()
        })]
      }));
    }
    return /*#__PURE__*/_jsxs(Flex, Object.assign({
      justifyContent: "space-between",
      alignItems: "baseline"
    }, dataAttributes, {
      "data-selenium-test": "task-due-date-input",
      children: [/*#__PURE__*/_jsx(Box, {
        marginInlineEnd: "200",
        grow: 1,
        children: this.shouldShowAbsoluteDateInput() ? this.renderAbsoluteDateInput() : this.renderRelativeDateInput()
      }), showTimeInput && /*#__PURE__*/_jsx(UIBox, {
        grow: 0,
        basis: "9em",
        children: this.renderTimeInput()
      })]
    }));
  }
}
TaskDueDateInput.propTypes = {
  'aria-labelledby': PropTypes.string,
  autoFocus: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    text: PropTypes.string
  })).isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  timeInputProps: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number
  }),
  dateInputProps: PropTypes.object,
  dateSelectProps: PropTypes.object,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  use: PropTypes.oneOf(['default', 'link', 'transparent', 'textOnly']),
  showTimeInput: PropTypes.bool
};
TaskDueDateInput.defaultProps = {
  showTimeInput: true
};